import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Camera, Eye, MapPin, RefreshCw, Wifi, WifiOff, X, 
  Image, Video, Car, Target, ZoomIn, ZoomOut,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Crosshair,
  Radio, Maximize2, Volume2, VolumeX
} from 'lucide-react';
import { fetchUserCameras, CameraData, API_BASE_URL, GO2RTC_URL } from '@/lib/authFetch';
import { toast } from 'sonner';
import { useLiveStreamName } from '@/hooks/useLiveStreamName';
import { 
  createWebRTCSession, 
  closeWebRTCSession, 
  attachStreamToVideo,
  checkStreamingHealth,
  WebRTCSession 
} from '@/lib/webrtcService';
// RecordingsPanel removido - não utilizado

const LiveCameras = () => {
  const [cameras, setCameras] = useState<CameraData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCamera, setSelectedCamera] = useState<CameraData | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Estado do modal de visualização
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [detectionResult, setDetectionResult] = useState<any>(null);
  const [detecting, setDetecting] = useState(false);
  
  // Estado do WebRTC
  const [viewMode, setViewMode] = useState<'snapshot' | 'webrtc'>('snapshot');
  const [webrtcSession, setWebrtcSession] = useState<WebRTCSession | null>(null);
  const [webrtcConnecting, setWebrtcConnecting] = useState(false);
  const [webrtcConnected, setWebrtcConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [streamingAvailable, setStreamingAvailable] = useState(false);
  const webrtcVideoRef = useRef<HTMLVideoElement>(null);
  const liveStreamName = useLiveStreamName(
    modalOpen && selectedCamera ? (selectedCamera.stream_id || `camera_${selectedCamera.id}`) : null
  );
  

  useEffect(() => {
    loadCameras();
    checkStreaming();
  }, []);

  // Atualizar lista ao voltar para a aba (nova câmera criada no admin aparece sem F5)
  useEffect(() => {
    const onFocus = () => loadCameras();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);
  
  // Verificar disponibilidade do streaming
  const checkStreaming = async () => {
    try {
      const health = await checkStreamingHealth();
      setStreamingAvailable(health.janus.status === 'online');
    } catch {
      setStreamingAvailable(false);
    }
  };

  const loadCameras = async () => {
    setLoading(true);
    try {
      // Dashboard do cliente: sempre usar /api/cameras (lista filtrada por permissão).
      // Não usar adminUser aqui: este componente é só da área do cliente; se o usuário
      // logou como admin antes, adminUser fica no localStorage e faria aparecer todas as câmeras.
      const data = await fetchUserCameras();
      setCameras(data);
    } catch (error) {
      toast.error('Erro ao carregar câmeras');
    } finally {
      setLoading(false);
    }
  };

  // Capturar snapshot da câmera
  const captureSnapshot = useCallback(async (cameraId: number) => {
    setLoadingSnapshot(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/cameras/${cameraId}/snapshot`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.image) {
          setSnapshotUrl(`data:image/jpeg;base64,${data.image}`);
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Falha ao capturar imagem');
      }
    } catch (error) {
      toast.error('Erro ao capturar snapshot');
    } finally {
      setLoadingSnapshot(false);
    }
  }, []);

  // Detectar veículos
  const handleDetectVehicles = async () => {
    if (!selectedCamera?.id) return;
    
    setDetecting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/cameras/${selectedCamera.id}/detectar-veiculos`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setDetectionResult(data);
        setSnapshotUrl(`data:image/jpeg;base64,${data.imagem_anotada}`);
        
        if (data.veiculos_detectados > 0) {
          const placas = data.veiculos.filter((v: any) => v.placa).map((v: any) => v.placa);
          toast.success(`${data.veiculos_detectados} veículo(s) detectado(s)${placas.length > 0 ? `. Placas: ${placas.join(', ')}` : ''}`);
        } else {
          toast.info('Nenhum veículo detectado na imagem');
        }
      } else {
        toast.error(data.detail || 'Erro na detecção');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao detectar veículos');
    } finally {
      setDetecting(false);
    }
  };

  // Controle PTZ
  const handlePTZ = async (action: string) => {
    if (!selectedCamera?.id) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/cameras/${selectedCamera.id}/ptz/${action}?speed=5`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      toast.error('Erro no controle PTZ');
    }
  };

  // Conectar WebRTC
  const connectWebRTC = async (camera: CameraData) => {
    if (!camera.id || webrtcConnecting) return;
    
    setWebrtcConnecting(true);
    setWebrtcConnected(false);
    
    try {
      const session = await createWebRTCSession({
        cameraId: camera.id,
        onTrack: (event) => {
          if (webrtcVideoRef.current && session.remoteStream) {
            webrtcVideoRef.current.srcObject = session.remoteStream;
          }
        },
        onConnectionStateChange: (state) => {
          if (state === 'connected') {
            setWebrtcConnected(true);
            setWebrtcConnecting(false);
            toast.success('Stream WebRTC conectado!');
          } else if (state === 'failed' || state === 'disconnected') {
            setWebrtcConnected(false);
            setWebrtcConnecting(false);
          }
        },
        onError: () => {
          setWebrtcConnecting(false);
          toast.error('Falha no stream WebRTC');
        }
      });
      
      setWebrtcSession(session);
      
      // Anexar stream ao vídeo
      if (webrtcVideoRef.current && session.remoteStream) {
        webrtcVideoRef.current.srcObject = session.remoteStream;
        webrtcVideoRef.current.play().catch(() => {});
      }
      
    } catch {
      setWebrtcConnecting(false);
      toast.error('Não foi possível iniciar o stream');
    }
  };
  
  // Desconectar WebRTC
  const disconnectWebRTC = async () => {
    if (selectedCamera?.id) {
      await closeWebRTCSession(selectedCamera.id);
    }
    setWebrtcSession(null);
    setWebrtcConnected(false);
    if (webrtcVideoRef.current) {
      webrtcVideoRef.current.srcObject = null;
    }
  };

  // Abrir modal - vai direto para streaming ao vivo
  const handleAssistir = async (camera: CameraData) => {
    setSelectedCamera(camera);
    setModalOpen(true);
    toast.success(`Conectando a ${camera.nome}...`);
  };

  // Fechar modal
  const handleCloseModal = async () => {
    // Desconectar WebRTC se estiver ativo
    if (webrtcSession) {
      await disconnectWebRTC();
    }
    
    setModalOpen(false);
    setSelectedCamera(null);
    setAutoRefresh(false);
    setSnapshotUrl(null);
    setDetectionResult(null);
    setViewMode('snapshot');
  };
  
  // Alternar modo de visualização
  const handleSwitchViewMode = async (mode: 'snapshot' | 'webrtc') => {
    if (mode === viewMode) return;
    
    if (mode === 'webrtc' && selectedCamera) {
      setViewMode('webrtc');
      await connectWebRTC(selectedCamera);
    } else {
      await disconnectWebRTC();
      setViewMode('snapshot');
      if (selectedCamera?.id) {
        await captureSnapshot(selectedCamera.id);
      }
    }
  };

  // Auto-refresh de snapshots
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (autoRefresh && selectedCamera?.id && modalOpen) {
      interval = setInterval(() => {
        captureSnapshot(selectedCamera.id!);
      }, 2000); // Atualiza a cada 2 segundos
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, selectedCamera, modalOpen, captureSnapshot]);

  // Contadores
  const onlineCount = cameras.filter(c => c.status === 'online').length;
  const offlineCount = cameras.filter(c => c.status === 'offline').length;

  return (
    <div className="space-y-6">
      <Card className="bg-protec-dark-light border-protec-gold/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <Camera className="w-5 h-5 text-protec-gold" />
                Câmeras ao Vivo
              </CardTitle>
              <CardDescription className="text-gray-300">
                Monitore locais estratégicos em tempo real
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {streamingAvailable && (
                <Badge className="bg-green-500/20 text-green-400 gap-1">
                  <Radio className="w-3 h-3" />
                  WebRTC Disponível
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-protec-dark rounded-lg p-2 sm:p-4 text-center border border-protec-gold/10">
              <Camera className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold mx-auto mb-1 sm:mb-2" />
              <p className="text-lg sm:text-2xl font-bold text-white">{cameras.length}</p>
              <p className="text-[10px] sm:text-xs text-gray-400">Total</p>
            </div>
            <div className="bg-protec-dark rounded-lg p-2 sm:p-4 text-center border border-green-500/20">
              <Wifi className="w-4 h-4 sm:w-5 sm:h-5 text-green-400 mx-auto mb-1 sm:mb-2" />
              <p className="text-lg sm:text-2xl font-bold text-white">{onlineCount}</p>
              <p className="text-[10px] sm:text-xs text-gray-400">Online</p>
            </div>
            <div className="bg-protec-dark rounded-lg p-2 sm:p-4 text-center border border-red-500/20">
              <WifiOff className="w-4 h-4 sm:w-5 sm:h-5 text-red-400 mx-auto mb-1 sm:mb-2" />
              <p className="text-lg sm:text-2xl font-bold text-white">{offlineCount}</p>
              <p className="text-[10px] sm:text-xs text-gray-400">Offline</p>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8 sm:py-12 text-gray-400">
              <RefreshCw className="w-8 h-8 sm:w-10 sm:h-10 animate-spin mx-auto mb-3" />
              <p className="text-sm sm:text-base">Carregando câmeras...</p>
            </div>
          ) : cameras.length === 0 ? (
            <div className="text-center py-8 sm:py-12 text-gray-400">
              <Camera className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 opacity-30" />
              <p className="text-base sm:text-lg">Nenhuma câmera disponível</p>
              <p className="text-xs sm:text-sm mt-2">Entre em contato com o administrador</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {cameras.map((camera) => (
                <Card 
                  key={camera.id} 
                  className="bg-protec-dark border-protec-gold/10 hover:border-protec-gold/40 transition-all duration-300 group"
                >
                  <CardContent className="p-4">
                    {/* Preview Area */}
                    <div 
                      className="aspect-video bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg mb-3 flex items-center justify-center relative overflow-hidden cursor-pointer"
                      onClick={() => (camera.ip || camera.stream_id) && handleAssistir(camera)}
                    >
                      <Camera className={`w-12 h-12 ${camera.status === 'online' ? 'text-green-500/40' : 'text-gray-700'}`} />
                      
                      {/* Status badge */}
                      <Badge 
                        className={`absolute top-2 right-2 text-xs ${
                          camera.status === 'online' 
                            ? 'bg-green-500 text-white' 
                            : 'bg-red-500/80 text-white'
                        }`}
                      >
                        {camera.status === 'online' ? (
                          <><div className="w-1.5 h-1.5 bg-white rounded-full mr-1.5 animate-pulse" /> ONLINE</>
                        ) : 'OFFLINE'}
                      </Badge>
                      
                      
                      {/* Hover overlay - câmera por IP ou por stream_id (go2rtc) */}
                      {(camera.ip || camera.stream_id) && camera.status === 'online' && (
                        <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <div className="text-center">
                            <Eye className="w-10 h-10 text-protec-gold mx-auto mb-2" />
                            <span className="text-white text-sm">Clique para visualizar</span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="space-y-2">
                      <h3 className="font-semibold text-white text-sm truncate pb-0.5 min-h-[1.25em]" title={camera.nome} style={{ paddingBottom: '2px' }}>
                        {camera.nome}
                      </h3>
                      
                      <p className="text-xs text-gray-400 flex items-center gap-1 truncate">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        {camera.localizacao || 'Sem localização'}
                      </p>

                      {/* Info técnica */}
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        {camera.resolucao && <Badge variant="outline" className="text-xs py-0">{camera.resolucao}</Badge>}
                      </div>
                      
                      {/* Botão Assistir - considera IP ou stream_id (go2rtc) */}
                      <Button 
                        size="sm" 
                        className={`w-full mt-2 ${
                          (camera.ip || camera.stream_id) && camera.status === 'online'
                            ? 'bg-protec-gold text-black hover:bg-protec-gold-light'
                            : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                        }`}
                        disabled={!(camera.ip || camera.stream_id) || camera.status !== 'online'}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAssistir(camera);
                        }}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        {!(camera.ip || camera.stream_id) ? 'Não configurada' : camera.status !== 'online' ? 'Offline' : 'Assistir'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* MODAL DE VISUALIZAÇÃO - STREAMING AO VIVO */}
      {modalOpen && selectedCamera && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/90 backdrop-blur-sm"
            onClick={handleCloseModal}
          />
          
          {/* Modal Content */}
          <div className="relative w-full max-w-4xl max-h-[95vh] bg-protec-dark border border-protec-gold/30 rounded-xl overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-2 sm:p-3 border-b border-protec-gold/20 bg-gradient-to-r from-protec-dark-light to-protec-dark">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${selectedCamera.status === 'online' ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                  <Camera className={`w-4 h-4 sm:w-5 sm:h-5 ${selectedCamera.status === 'online' ? 'text-green-400' : 'text-red-400'}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-white font-bold text-sm sm:text-lg truncate pb-0.5 min-h-[1.25em]" style={{ paddingBottom: '2px' }} title={selectedCamera.nome}>{selectedCamera.nome}</h2>
                </div>
              </div>
              
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                <Badge className="bg-green-500 text-white text-xs">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full mr-1 sm:mr-2 animate-pulse" />
                  <span className="hidden sm:inline">Online</span>
                </Badge>
                
                <button
                  className="text-gray-400 hover:text-white transition-colors text-lg sm:text-xl px-1 sm:px-2"
                  onClick={handleCloseModal}
                >
                  ✕
                </button>
              </div>
            </div>
            
            {/* Área de Vídeo - Streaming WebRTC */}
            <div className="bg-black relative aspect-video">
              <div className="relative w-full h-full overflow-hidden">
                {liveStreamName && <iframe
                  src={`${GO2RTC_URL}/stream.html?src=${liveStreamName}&mode=mse,hls,mjpeg&video`}
                  className="border-0 overflow-hidden absolute inset-0"
                  allow="autoplay"
                  scrolling="no"
                  muted
                  style={{ 
                    background: 'black',
                    width: 'calc(100% + 40px)',
                    height: 'calc(100% + 60px)',
                    marginTop: '-10px',
                    marginLeft: '-10px'
                  }}
                />}
                {/* Indicador Ao Vivo */}
                <div className="absolute top-2 sm:top-3 left-2 sm:left-3 bg-red-600 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs flex items-center gap-1 sm:gap-1.5 shadow-lg z-10">
                  <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full animate-pulse"></span>
                  AO VIVO
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveCameras;
